/**
 * Cookie Consent Management for WITAL.AI
 *
 * Implements GDPR/CCPA compliant cookie consent with Google Analytics integration
 * and notification service data collection consent.
 *
 * Suppressed inside the Expo WebView shell (both iOS and Android): the trackers
 * this banner gates (Google Analytics, AdSense) are already skipped in
 * index.html for native shells per App Store guideline 5.1.2(i), so showing a
 * cookie prompt there is misleading and caused the build 10 rejection.
 */

import 'vanilla-cookieconsent/dist/cookieconsent.css'
import * as CookieConsent from 'vanilla-cookieconsent'
import { isReactNativeWebView } from './nativeBridge'

interface ConsentPreferences {
  analytics: boolean
  marketing: boolean
  notifications: boolean
}

class CookieConsentService {
  private static instance: CookieConsentService
  private isInitialized = false
  private currentLanguage = 'en'

  static getInstance(): CookieConsentService {
    if (!CookieConsentService.instance) {
      CookieConsentService.instance = new CookieConsentService()
    }
    return CookieConsentService.instance
  }

  /**
   * Initialize cookie consent with WITAL's configuration.
   * Skipped inside the Expo WebView shell — see file-level comment.
   */
  async initialize(language: string = 'en'): Promise<void> {
    if (isReactNativeWebView()) {
      console.log('🍪 Skipping cookie consent inside the Expo WebView shell')
      return
    }

    if (this.isInitialized) {
      return
    }

    this.currentLanguage = language
    
    const config = this.getConsentConfig(language)
    
    await CookieConsent.run(config)
    
    // Set up event listeners for consent changes
    this.setupEventListeners()
    
    this.isInitialized = true
    console.log('🍪 Cookie consent initialized for WITAL.AI')
  }

  /**
   * Get current consent preferences
   */
  getConsentPreferences(): ConsentPreferences {
    const cookie = CookieConsent.getCookie()
    return {
      analytics: cookie?.categories?.includes('analytics') || false,
      marketing: cookie?.categories?.includes('marketing') || false,
      notifications: cookie?.categories?.includes('notifications') || false
    }
  }

  /**
   * Check if Google Analytics is allowed
   */
  isAnalyticsAllowed(): boolean {
    return this.getConsentPreferences().analytics
  }

  /**
   * Check if marketing cookies are allowed
   */
  isMarketingAllowed(): boolean {
    return this.getConsentPreferences().marketing
  }

  /**
   * Check if notification data collection is allowed
   */
  isNotificationsAllowed(): boolean {
    return this.getConsentPreferences().notifications
  }

  /**
   * Show the consent modal
   */
  showModal(): void {
    CookieConsent.showPreferences()
  }

  /**
   * Update language and reinitialize
   */
  async updateLanguage(language: string): Promise<void> {
    if (this.currentLanguage === language) {
      return
    }
    
    this.currentLanguage = language
    const config = this.getConsentConfig(language)
    
    // Update the existing instance
    await CookieConsent.run(config)
  }

  /**
   * Configure cookie consent for WITAL's needs
   */
  private getConsentConfig(language: string): CookieConsent.CookieConsentConfig {
    const translations = this.getTranslations(language)

    return {
      cookie: {
        name: 'cc_cookie',
        useLocalStorage: true
      },

      guiOptions: {
        consentModal: {
          layout: 'box inline',
          position: 'bottom right'
        },
        preferencesModal: {
          layout: 'box',
          position: 'right',
          equalWeightButtons: true,
          flipButtons: false
        }
      },

      categories: {
        necessary: {
          enabled: true,
          readOnly: true
        },
        analytics: {
          enabled: false,
          readOnly: false,
          autoClear: {
            cookies: [
              {
                name: /^(_ga|_gid|_gat|gtag|_gcl_)/
              }
            ]
          }
        },
        marketing: {
          enabled: false,
          readOnly: false,
          autoClear: {
            cookies: [
              {
                name: /^(_fbp|_fbc|fr)/
              }
            ]
          }
        },
        notifications: {
          enabled: false,
          readOnly: false
        }
      },

      language: {
        default: language,
        translations: {
          [language]: translations
        }
      }
    }
  }

  /**
   * Get translations for different languages
   */
  private getTranslations(language: string) {
    const translations: Record<string, any> = {
      en: {
        consentModal: {
          title: 'We use cookies! 🍪',
          description: 'WITAL.AI uses cookies to improve your experience. We use analytics to understand usage patterns and may collect data for weather notifications if you enable them.',
          acceptAllBtn: 'Accept All',
          acceptNecessaryBtn: 'Accept Necessary Only',
          showPreferencesBtn: 'Manage Preferences',
          footer: '<a href="#privacy">Privacy Policy</a>'
        },
        preferencesModal: {
          title: 'Cookie Preferences',
          acceptAllBtn: 'Accept All',
          acceptNecessaryBtn: 'Accept Necessary Only',
          savePreferencesBtn: 'Save Preferences',
          closeIconLabel: 'Close',
          serviceCounterLabel: 'Service|Services',
          sections: [
            {
              title: 'Cookie Usage 📢',
              description: 'We use cookies to enhance your weather forecasting experience and understand how you use WITAL.AI.'
            },
            {
              title: 'Strictly Necessary Cookies <span class="pm__badge">Always Enabled</span>',
              description: 'These cookies are essential for WITAL.AI to function. They store your language preferences and city selections.',
              linkedCategory: 'necessary'
            },
            {
              title: 'Analytics Cookies',
              description: 'These cookies help us understand how visitors interact with WITAL.AI by collecting and reporting information anonymously.',
              linkedCategory: 'analytics',
              cookieTable: {
                headers: {
                  name: 'Name',
                  domain: 'Domain',
                  description: 'Description'
                },
                body: [
                  {
                    name: '_ga',
                    domain: '.wital.ai',
                    description: 'Google Analytics - Distinguishes unique users'
                  },
                  {
                    name: '_gid',
                    domain: '.wital.ai',
                    description: 'Google Analytics - Distinguishes unique users (24 hours)'
                  }
                ]
              }
            },
            {
              title: 'Marketing Cookies',
              description: 'These cookies are used to make advertising messages more relevant to you and may be set by third-party advertising networks.',
              linkedCategory: 'marketing'
            },
            {
              title: 'Notification Data',
              description: 'When you enable weather notifications, we collect your email or device token to send you personalized weather alerts.',
              linkedCategory: 'notifications'
            },
            {
              title: 'More Information',
              description: 'For questions about our cookie policy, please <a class="cc__link" href="mailto:hello@wital.ai">contact us</a>.'
            }
          ]
        }
      },
      zh: {
        consentModal: {
          title: '我们使用Cookie！🍪',
          description: 'WITAL.AI 使用Cookie来改善您的体验。我们使用分析工具来了解使用模式，如果您启用天气通知，我们可能会收集相关数据。',
          acceptAllBtn: '全部接受',
          acceptNecessaryBtn: '仅接受必要',
          showPreferencesBtn: '管理偏好',
          footer: '<a href="#privacy">隐私政策</a>'
        },
        preferencesModal: {
          title: 'Cookie偏好设置',
          acceptAllBtn: '全部接受',
          acceptNecessaryBtn: '仅接受必要',
          savePreferencesBtn: '保存偏好',
          closeIconLabel: '关闭',
          serviceCounterLabel: '服务',
          sections: [
            {
              title: 'Cookie使用说明 📢',
              description: '我们使用Cookie来增强您的天气预报体验并了解您如何使用WITAL.AI。'
            },
            {
              title: '严格必要的Cookie <span class="pm__badge">始终启用</span>',
              description: '这些Cookie对WITAL.AI的正常运行至关重要。它们存储您的语言偏好和城市选择。',
              linkedCategory: 'necessary'
            },
            {
              title: '分析Cookie',
              description: '这些Cookie通过匿名收集和报告信息帮助我们了解访问者如何与WITAL.AI互动。',
              linkedCategory: 'analytics'
            },
            {
              title: '营销Cookie',
              description: '这些Cookie用于使广告消息与您更相关，可能由第三方广告网络设置。',
              linkedCategory: 'marketing'
            },
            {
              title: '通知数据',
              description: '当您启用天气通知时，我们收集您的电子邮件或设备令牌以向您发送个性化天气警报。',
              linkedCategory: 'notifications'
            },
            {
              title: '更多信息',
              description: '如有关于我们Cookie政策的问题，请<a class="cc__link" href="mailto:hello@wital.ai">联系我们</a>。'
            }
          ]
        }
      }
    }

    return translations[language] || translations.en
  }

  /**
   * Set up event listeners for consent changes
   */
  private setupEventListeners(): void {
    // Listen for consent changes using addEventListener
    document.addEventListener('cc:onConsent', (event: any) => {
      const { cookie } = event.detail
      console.log('🍪 Consent updated:', cookie.categories)
      
      // Handle Google Analytics
      if (cookie.categories.includes('analytics')) {
        this.enableGoogleAnalytics()
      } else {
        this.disableGoogleAnalytics()
      }

      // Handle marketing cookies. The else branch matters: without it,
      // withdrawing advertising consent left ad_storage / ad_user_data /
      // ad_personalization 'granted' in gtag for the rest of the session,
      // so the cookie settings appeared to revoke and did not.
      if (cookie.categories.includes('marketing')) {
        this.enableMarketing()
      } else {
        this.disableMarketing()
      }

      // Handle notification consent
      if (cookie.categories.includes('notifications')) {
        this.enableNotifications()
      }

      // Emit custom event for app components
      window.dispatchEvent(new CustomEvent('cookieConsentChanged', {
        detail: {
          analytics: cookie.categories.includes('analytics'),
          marketing: cookie.categories.includes('marketing'),
          notifications: cookie.categories.includes('notifications')
        }
      }))
    })

    // Listen for first consent
    document.addEventListener('cc:onFirstConsent', (event: any) => {
      const { cookie } = event.detail
      console.log('🍪 First consent given:', cookie.categories)
    })
  }

  /**
   * Enable Google Analytics tracking
   */
  private enableGoogleAnalytics(): void {
    console.log('📊 Enabling Google Analytics')
    
    // Initialize gtag if not already done
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted'
      })
    }
  }

  /**
   * Disable Google Analytics tracking
   */
  private disableGoogleAnalytics(): void {
    console.log('📊 Disabling Google Analytics')
    
    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied'
      })
    }
  }

  /**
   * Enable marketing cookies
   */
  private enableMarketing(): void {
    console.log('📢 Enabling marketing cookies')

    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: 'granted',
        ad_user_data: 'granted',
        ad_personalization: 'granted'
      })
    }
  }

  /**
   * Disable marketing cookies
   */
  private disableMarketing(): void {
    console.log('📢 Disabling marketing cookies')

    if (typeof window.gtag === 'function') {
      window.gtag('consent', 'update', {
        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied'
      })
    }
  }

  /**
   * Enable notification data collection
   */
  private enableNotifications(): void {
    console.log('🔔 Enabling notification data collection')
    // This will be used by the notification service
  }
}

// Export singleton instance
export const cookieConsent = CookieConsentService.getInstance()
export default cookieConsent